/**
 * Read + mutate the user's decision-memory rules.
 *
 * Wraps the backend endpoints:
 *
 *   - **GET** `/api/orgs/<orgId>/memory/rules/` returns
 *     `{rules: AgentMemoryRule[]}`.
 *   - **PATCH** `/api/orgs/<orgId>/memory/rules/<ruleId>/` vetoes a rule
 *     (no request body needed — the backend ignores it).
 *   - **POST** `/api/orgs/<orgId>/memory/wipe/` wipes all rules.
 *
 * Flag-off or non-member both surface as HTTP 404.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/lib/errors';
import { apiGet, apiPatch, apiPost } from '@/lib/http/client';

export type AgentMemoryRuleStatus = 'active' | 'candidate' | 'vetoed';

export interface AgentMemoryEvidence {
  proposal_id: number;
  decision_type: string;
  resolved_at: string | null;
  summary: string | null;
}

export interface AgentMemoryRule {
  id: string;
  text: string;
  scope: string;
  status: AgentMemoryRuleStatus;
  confidence: number;
  first_seen: string;
  last_confirmed: string;
  evidence: AgentMemoryEvidence[];
  retired_reason: string | null;
}

interface MemoryRulesResponse {
  rules: AgentMemoryRule[];
}

/**
 * Stable key factory keeps mutation invalidation paired with the query.
 * Exported so consumers (e.g. tests) can target the cache directly.
 */
export const agentMemoryQueryKey = (orgId: number | undefined) =>
  ['agentMemory', orgId] as const;

export function useAgentMemory(orgId: number | undefined) {
  return useQuery({
    queryKey: agentMemoryQueryKey(orgId),
    queryFn: () =>
      apiGet<MemoryRulesResponse>(`/api/orgs/${orgId}/memory/rules/`),
    select: response => response.rules,
    enabled: !!orgId,
    staleTime: 60_000,
    retry: (failureCount, error) => {
      if (
        error instanceof ApiError &&
        error.statusCode >= 400 &&
        error.statusCode < 500
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

export function useVetoMemoryRule(orgId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ruleId: string) =>
      apiPatch(`/api/orgs/${orgId}/memory/rules/${ruleId}/`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentMemoryQueryKey(orgId) });
    },
  });
}

export function useWipeMemory(orgId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost(`/api/orgs/${orgId}/memory/wipe/`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentMemoryQueryKey(orgId) });
    },
  });
}
